import { useRef, useState } from "react";
import { createThreadShare } from "duck/endpoints/threadShares";
import { useChatContext } from "duck/ui/chat/contexts/useChatContext";
import { useTranslation } from "react-i18next";

import { toast } from "features/ui/Toast";

import ShareLinkToast from "./ShareLinkToast";
import { buildShareURL, copyShareURL } from "./utils";

export const useShareThread = () => {
  const { t } = useTranslation();
  const {
    currentThreadId,
    messages,
    isStreaming,
    isAnySubthreadStreaming,
    isLoadingCurrentThread,
  } = useChatContext();

  const [isSharing, setIsSharing] = useState(false);
  // Each call mints a separate valid share link, so a second click must not
  // start before the first settles. The ref catches clicks fired within the
  // same tick, which `isSharing` alone cannot see until the next render.
  const shareInFlightRef = useRef(false);

  // Sharing captures the thread as-is, so wait until the thread is finished.
  const isDisabled =
    isStreaming ||
    isAnySubthreadStreaming ||
    isLoadingCurrentThread ||
    isSharing ||
    messages.length === 0;

  const shareThread = async () => {
    if (!currentThreadId || shareInFlightRef.current) {
      return;
    }

    shareInFlightRef.current = true;
    setIsSharing(true);

    try {
      const share = await createThreadShare(currentThreadId);
      const shareURL = buildShareURL(share.id);

      if (await copyShareURL(shareURL)) {
        toast.success(t("duck:share_thread_link_copied"));
      } else {
        // Kept on screen with its own copy button: that click carries the user
        // activation the clipboard needs, which awaiting the request lost.
        toast.error(<ShareLinkToast url={shareURL} />, { autoClose: null });
      }
    } catch {
      toast.error(t("duck:share_thread_error"));
    } finally {
      shareInFlightRef.current = false;
      setIsSharing(false);
    }
  };

  return { currentThreadId, isDisabled, shareThread };
};
